import React, { useEffect, useState } from 'react';
import { Box, useColorMode } from 'native-base';
import { faAngleLeft, faMoon, faSun, faBars } from '@fortawesome/free-solid-svg-icons';

import { SpIcon } from './Icon';
import { LogoMd } from './Image';
import Pressable from './Pressable';
import { Caption, H2 } from './Typography';
import { useTopNavigationStyles } from './styles';
import routes, { AppRouteProps, useAppNavigate } from '~/pages';
import { useAuthorization } from '~/hooks/auth';
import Loading from './Loading';
import { BottomNav, I18nReacher, useAppSettings, useI18n, useSpringBigStore } from '@springbig/shared';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { OpenAppButton } from './GradientButton/OpenAppButton';

type TopNavigationProps = {
    toggleMenu: () => void;
    isMenuOpen: boolean;
    isBack?: boolean;
}

const TopNavigation = (props: TopNavigationProps) => {

    const { merchant, member, walletId, apiEnv } = useSpringBigStore();
    const { icon, background, divider, text } = useTopNavigationStyles();
    const { colorMode, toggleColorMode } = useColorMode();
    const { location, navigate } = useAppNavigate();
    const { switchLightDarkMode, upperLeftIcon, isDisabledSideNav, merchantOrg, playStoreURL, appStoreURL } = useAppSettings();
    const { isBack } = props;
    const currentPath = location.pathname;
    const goBackRoute = routes.find(route => `/${route.path}` === currentPath)?.goBackPath;
    const navigator = useNavigate();
    const { isLoadingAuth } = useAuthorization();
    const { titleOnTop, bottomNav } = useAppSettings();
    const [title, setTitle] = useState<string | undefined>(undefined);
    const { t } = useI18n();

    const isRouteProps = (item: BottomNav | RouteProps): item is RouteProps => {

        return (item as RouteProps)?.title !== undefined;
    };

    const isBottomNav = (item: BottomNav | RouteProps): item is BottomNav => {

        return (item as BottomNav)?.i18n !== undefined;
    };

    useEffect(() => {

        if (titleOnTop) {

            const isSettingsBottomNavInValid = bottomNav.some(obj => obj.path === undefined);
            let _items: (BottomNav | RouteProps)[] = (bottomNav && !isSettingsBottomNavInValid) ? bottomNav : routes.filter(el => el.showTab);

            if (merchant && member) {

                if (!member.subscriptions_enabled) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Subscriptions');
                }

                if (!member.gift_card_enabled) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'SpringbigPay');
                }

                if (!member.show_milestones) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Milestones');
                }

                if (!member.show_rewards) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Rewards');
                }

                if (!member.merchant_website) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Cart');
                }

                if (!member.referrals?.active) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Refer');
                }

                if (!merchant.dutchie_plus_enabled) {
                    _items = _items.filter(t => ((t as RouteProps).title || (t as BottomNav).toScreen) !== 'Shop');
                }
            }

            const bottomNavItem = _items.find((value) => '/' + value?.path?.replaceAll(':wallet_id', walletId) === currentPath);
            const routesNavItem = routes.find((value) => '/' + value?.path?.replaceAll(':wallet_id', walletId) === currentPath);
            if (routesNavItem && isRouteProps(routesNavItem)) {
                setTitle(routesNavItem?.title);
            }

            if (bottomNavItem && isBottomNav(bottomNavItem)) {
                setTitle(t(bottomNavItem.i18n));
            }
        }
    }, [bottomNav, currentPath, titleOnTop]);

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    const handleNavigation = (route?: string) => {

        if (isBack) {
            goBackRoute && navigate(goBackRoute);
        }
        else if (route && walletId) {
            navigator(`/w/${walletId}/${route}` as AppRouteProps, {});
        }
    };

    const onLeftIconPress = () => {

        if (upperLeftIcon) {

            if (upperLeftIcon.toUrl && member) {
                window.open(upperLeftIcon.toUrl, '_blank');
                return;
            }

            handleNavigation(upperLeftIcon.toScreen);
            return;
        }

        handleNavigation();
    };

    return (
        <Box
            flexDirection={'row'}
            justifyContent="space-between"
            alignItems="center"
            p={4}
            w={'100%'}
            zIndex={9999}
            bg={background}
            borderBottomColor={divider}
        >
            { apiEnv !== 'production' &&
                <Caption
                    position={'absolute'}
                    top={0}
                    left={0}
                    right={0}
                    textAlign={'center'}

                >
                    <Caption color={member ? 'green' : 'red'}>●</Caption>
                    <Caption color={merchant ? 'green' : 'red'}>●</Caption>
                </Caption>
            }
            <Pressable
                onPress={onLeftIconPress}
            >
                {goBackRoute
                    ? <Box flexDirection={'row'} alignItems="center" >
                        <SpIcon color={icon} icon={faAngleLeft} />
                        <Caption
                            color={text}
                            fontSize={16}
                            text={'general.back'}
                        />
                    </Box>
                    : <LogoMd resizeMode="contain" ml={4} />}
            </Pressable>

            { title &&
                <H2
                    color={text}
                > {title} </H2> }

            <Box alignItems={'center'} flexDirection={'row'}>
                {merchantOrg && playStoreURL && appStoreURL ?
                    <OpenAppButton appScheme={merchantOrg + '://'} playStoreURL={playStoreURL} appStoreURL={appStoreURL}/>
                    : <></>
                }
                {switchLightDarkMode && (
                    <Pressable
                        onPress={toggleColorMode}
                        borderRadius={50}
                        p={1}
                        mr={6}
                    >
                        {colorMode === 'light' ? <SpIcon
                            size={20}
                            color={icon}
                            icon={faSun}
                        /> : <SpIcon
                            color={icon}
                            size={20}
                            icon={faMoon}
                        />
                        }
                    </Pressable>
                )}
                {!isDisabledSideNav && (
                    <Pressable
                        testID="hamburger-menu"
                        borderRadius={50}
                        p={1}
                        onPress={props.toggleMenu}
                    >
                        {
                            props.isMenuOpen ?
                                <SpIcon
                                    color={icon}
                                    size={20}
                                    icon={faXmark}
                                />
                                : <SpIcon
                                    color={icon}
                                    size={20}
                                    icon={faBars}
                                />}
                    </Pressable>
                )}
            </Box>
        </Box>
    );
};

export default TopNavigation;
